/**
 * useAuth - Hook for accessing current authentication state.
 *
 * Validates the stored JWT token against the backend on first load.
 * Returns cached user data for subsequent renders (staleTime: Infinity).
 */

import { useQuery } from '@tanstack/react-query';
import { authQueryKeys, getStoredToken, getStoredUser, clearAuth } from '../utils/authStorage';
import { ApiError, fetchCurrentUser } from '@/lib/api';
import type { AuthUser } from '../types';

/**
 * Validate the stored token by calling GET /auth/me.
 *
 * - If no token exists, returns null immediately (no network request).
 * - If the token is valid, returns the user.
 * - If the token is expired/invalid (401), handleResponse triggers
 *   handleAuthError which clears localStorage and redirects to /login.
 * - Any other failure (502/503 while the API restarts, a network error) says
 *   nothing about the token, so the stored session is kept (shipcast-45y.11).
 *   The next request that gets a 401 still ends it.
 */
async function validateAndGetUser(): Promise<AuthUser | null> {
  const token = getStoredToken();
  if (!token) {
    return null;
  }

  try {
    const user = await fetchCurrentUser();
    return user;
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 401) {
      // handleResponse already cleared storage; return null so React Query
      // reflects the logged-out state.
      clearAuth();
      return null;
    }
    return getStoredUser();
  }
}

/**
 * Hook to get the current authentication state.
 *
 * On first mount, validates the stored JWT against the backend.
 * After validation succeeds, the result is cached indefinitely
 * (until logout or a 401 response clears the cache).
 *
 * @returns Object containing:
 * - `user`: Current authenticated user or null
 * - `isAuthenticated`: Boolean indicating if user is logged in
 * - `isLoading`: Boolean indicating if auth state is being validated
 */
export function useAuth() {
  const { data: user, isLoading } = useQuery({
    queryKey: authQueryKeys.currentUser(),
    queryFn: validateAndGetUser,
    staleTime: Infinity, // Once validated, don't re-validate until cache is cleared
    gcTime: Infinity, // Never garbage collect auth state
    retry: false, // Don't retry auth validation — expired means expired
  });

  return {
    user: user ?? null,
    isAuthenticated: !!user,
    isLoading,
  };
}
